import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { usePopupStore, useSearchStore } from '../..';
import useNavigateToLead from '../../hooks/useNavigateToLead';
import ILead from '../../interfaces/ILead';
import ITenantInMarket from '../../interfaces/ITenantInMarket';
import PopupType from '../../interfaces/PopupType';
import SearchType from '../../interfaces/SearchType';
import { leadStatusToString } from '../../utils/enumUtils';
import { calculateVariance, formatDate, formatNumber } from '../../utils/utils';
import commonStyles from "../Common.module.css";
import Grid, { IHeader } from '../Grid';

function LeadsGrid({ leads, tenantInMarket }: ILeadsGridProps) {
    const searchStore = useSearchStore();
    const popupStore = usePopupStore();
    const navigateToLead = useNavigateToLead();
    const landNeeded = useMemo(() => tenantInMarket ? (tenantInMarket.landAreaMin + tenantInMarket.landAreaMax) / 2 : null, [tenantInMarket]);
    const buildNeeded = useMemo(() => tenantInMarket ? (tenantInMarket.buildingAreaMin + tenantInMarket.buildingAreaMax) / 2 : null, [tenantInMarket]);

    const leadsWithVariance = useMemo(() => leads.map<ILeadWithVariance>(item => ({
        ...item,
        variance: tenantInMarket
            ? calculateVariance(item.landSize, item.buildingSize, landNeeded, buildNeeded)
            : null,
    })), [leads, tenantInMarket, landNeeded, buildNeeded]);

    return (
        <Grid
            data={_.orderBy(leadsWithVariance, d => d.variance, "asc")}
            headers={[
                ...(!tenantInMarket ? [] : [
                    {
                        align: "left",
                        titleStrings: ["VARIANCE"],
                        getValue: item => item.variance,
                        getLabel: item => item.variance !== null ? formatNumber(item.variance) : "",
                    } as IHeader<ILeadWithVariance>
                ]),
                {
                    align: "left",
                    titleStrings: ["OWNER NAME"],
                    getValue: item => item.ownerName,
                    getLabel: item => item.ownerName || "",
                    onClick: item => searchStore.setSearch(item.ownerName!, SearchType.Leads),
                },
                {
                    align: "left",
                    isFullWidth: true,
                    titleStrings: ["ADDRESS"],
                    getValue: item => item.address,
                    getLabel: item => item.address || "",
                },
                {
                    titleStrings: ["STATUS"],
                    getValue: item => item.status,
                    getLabel: item => leadStatusToString(item.status),
                },
                {
                    titleStrings: ["ZONING"],
                    getValue: item => item.zoning,
                    getLabel: item => item.zoning || "",
                },
                {
                    titleStrings: ["BUILDING", "SIZE"],
                    getValue: item => item.buildingSize,
                    getLabel: item => item.buildingSize ? formatNumber(item.buildingSize) : "",
                },
                {
                    titleStrings: ["LAND", "SIZE"],
                    getValue: item => item.landSize,
                    getLabel: item => item.landSize ? formatNumber(item.landSize) : "",
                },
                {
                    titleStrings: ["USABLE", "LAND SIZE"],
                    getValue: item => item.usableArea,
                    getLabel: item => item.usableArea ? formatNumber(item.usableArea) : "",
                },
                {
                    titleStrings: ["LOT", "COVERAGE"],
                    getValue: item => item.lotCoverage,
                    getLabel: item => item.lotCoverage ? `${item.lotCoverage}%` : "",
                },
                {
                    titleStrings: ["LOCATION", "GRADE"],
                    getValue: item => item.locationGrade,
                    getLabel: item => item.locationGrade || "",
                },
                {
                    titleStrings: ["PROPERTY", "GRADE"],
                    getValue: item => item.propertyGrade,
                    getLabel: item => item.propertyGrade || "",
                },
                {
                    titleStrings: ["MODIFIED BY"],
                    getValue: item => item.modifiedByUser,
                    getLabel: item => item.modifiedByUser || "",
                },
                {
                    titleStrings: ["LAST MODIFIED"],
                    getValue: item => item.modifiedDate,
                    getLabel: item => item.modifiedDate ? formatDate(item.modifiedDate) : "",
                },
            ]}
            getId={item => item.id}
            downloadFileName={"Leads"}
            onRowClick={item => navigateToLead(item.id)}
            isRowActive={item => popupStore.type === PopupType.Lead && popupStore.id === item.id}
            header={<div className={[commonStyles.gridTitle, commonStyles.header].join(" ")}>{leads.length} Leads</div>}
        />
    );
}

export default observer(LeadsGrid);

export interface ILeadWithVariance extends ILead {
    variance: number | null;
}

interface ILeadsGridProps {
    leads: ILead[];
    tenantInMarket?: ITenantInMarket;
}
